import React, { useState, useEffect } from 'react';
import { Heart, Repeat, CreditCard, CheckCircle } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';

const GivePage = () => {
  const [selectedOption, setSelectedOption] = useState('one-time');
  const [amount, setAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');

  const predefinedAmounts = [10, 25, 50, 100, 250, 500];

  const handleQuickPayment = async (paymentType) => {
    const finalAmount = amount || customAmount;
    if (!finalAmount || parseFloat(finalAmount) <= 0) {
      alert('Please select or enter an amount');
      return;
    }

    // Simply redirect to Stripe Checkout which will show Apple Pay/Google Pay on supported devices
    try {
      const token = localStorage.getItem('access_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const originUrl = window.location.origin;
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${backendUrl}/api/payments/checkout`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          package_id: 'donation',
          origin_url: originUrl,
          custom_amount: parseFloat(finalAmount)
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe Checkout - Apple Pay/Google Pay will show if device supports it
        window.location.href = data.url;
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to process donation. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('An error occurred. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <Heart className="w-16 h-16 mx-auto mb-6 text-purple-400" />
          <h1 className="text-5xl font-light mb-6">Partner With Us</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Your generosity helps us raise and equip the next generation of Kingdom leaders. Every gift makes a lasting impact in the lives of those we mentor.
          </p>
        </div>
      </div>

      {/* Giving Options */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Toggle Between One-time and Monthly */}
        <div className="bg-white rounded-2xl shadow-sm p-2 inline-flex mb-12">
          <button
            onClick={() => setSelectedOption('one-time')}
            className={`px-8 py-3 rounded-xl font-medium transition-all ${
              selectedOption === 'one-time'
                ? 'bg-black text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <CreditCard className="w-5 h-5 inline mr-2" />
            One-Time Gift
          </button>
          <button
            onClick={() => setSelectedOption('monthly')}
            className={`px-8 py-3 rounded-xl font-medium transition-all ${
              selectedOption === 'monthly'
                ? 'bg-black text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Repeat className="w-5 h-5 inline mr-2" />
            Monthly Partner
          </button>
        </div>

        {/* Amount Selection */}
        <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">
            {selectedOption === 'one-time' ? 'Select Amount' : 'Monthly Commitment'}
          </h2>

          {/* Predefined Amounts */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {predefinedAmounts.map((value) => (
              <button
                key={value}
                onClick={() => setAmount(value.toString())}
                className={`py-4 rounded-xl font-medium transition-all ${
                  amount === value.toString()
                    ? 'bg-black text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                £{value}
              </button>
            ))}
          </div>

          {/* Custom Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Or Enter Custom Amount
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg">
                £
              </span>
              <input
                type="number"
                placeholder="0.00"
                value={customAmount}
                onChange={(e) => {
                  setCustomAmount(e.target.value);
                  setAmount('');
                }}
                className="w-full pl-10 pr-4 py-4 border border-gray-300 rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Payment Form Placeholder */}
        <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">Payment Information</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
              <input
                type="text"
                placeholder="John Doe"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                placeholder="john@example.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Card Number</label>
              <input
                type="text"
                placeholder="4242 4242 4242 4242"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Date</label>
                <input
                  type="text"
                  placeholder="MM/YY"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CVV</label>
                <input
                  type="text"
                  placeholder="123"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Payment Buttons */}
        {(amount || customAmount) && parseFloat(amount || customAmount) > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">Quick Payment</h3>
            <p className="text-sm text-gray-600 mb-6 text-center">Pay instantly with Apple Pay or Google Pay</p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={() => handleQuickPayment('apple')}
                className="flex items-center justify-center bg-black text-white py-4 rounded-xl text-lg font-medium hover:bg-gray-800 transition-colors border-2 border-black"
              >
                <svg className="w-6 h-6 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                Apple Pay
              </button>

              <button
                onClick={() => handleQuickPayment('google')}
                className="flex items-center justify-center bg-white text-gray-900 py-4 rounded-xl text-lg font-medium hover:bg-gray-50 transition-colors border-2 border-gray-300"
              >
                <svg className="w-6 h-6 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Google Pay
              </button>
            </div>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">OR</span>
              </div>
            </div>
          </div>
        )}

        {/* Card Payment Button */}
        <button 
          onClick={async () => {
            const finalAmount = amount || customAmount;
            if (!finalAmount || parseFloat(finalAmount) <= 0) {
              alert('Please select or enter an amount');
              return;
            }
            
            try {
              const token = localStorage.getItem('access_token');
              const backendUrl = process.env.REACT_APP_BACKEND_URL;
              const originUrl = window.location.origin;
              
              // Prepare headers - include auth token if available
              const headers = {
                'Content-Type': 'application/json'
              };
              
              if (token) {
                headers['Authorization'] = `Bearer ${token}`;
              }
              
              const response = await fetch(`${backendUrl}/api/payments/checkout`, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({
                  package_id: 'donation',
                  origin_url: originUrl,
                  custom_amount: parseFloat(finalAmount)
                })
              });

              if (response.ok) {
                const data = await response.json();
                // Redirect to Stripe checkout
                window.location.href = data.url;
              } else {
                const error = await response.json();
                alert(error.detail || 'Failed to process donation. Please try again.');
              }
            } catch (error) {
              console.error('Error:', error);
              alert('An error occurred. Please try again.');
            }
          }}
          className="w-full bg-black text-white py-4 rounded-xl text-lg font-medium hover:bg-gray-800 transition-colors flex items-center justify-center space-x-2"
        >
          <CreditCard className="w-5 h-5" />
          <span>Pay with Card - {selectedOption === 'one-time' ? 'One-Time' : 'Monthly'} Gift
          {(amount || customAmount) && ` of £${amount || customAmount}`}</span>
        </button>

        <p className="text-center text-gray-500 text-sm mt-6">
          Payment processing powered by Stripe. Your information is secure and encrypted.
        </p>
      </div>
    </div>
  );
};

export default GivePage;